/**
 * Hook for the dispatch board day endpoint.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DispatchDayResponse } from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

/** Shared query options so a prefetch warms the exact entry the hook reads. */
export function dispatchDayOptions(date: string) {
  return queryOptions({
    queryKey: ['dispatch', 'day', date],
    queryFn: () => apiCall<DispatchDayResponse>(`/dispatch/day?date=${date}`),
    staleTime: 10_000,
    refetchInterval: 60_000,
  });
}

export function useDispatchDay(date: string) {
  return useQuery(dispatchDayOptions(date));
}

/** A committed task that still has no scheduled time (§D safety net). */
export interface NeedsSchedulingTask {
  task_id: string;
  task_title: string;
  task_type: string;
  project_id: string;
  project_title: string;
  project_status: string;
}

/** A committed project with no crew assigned anywhere (D-3/A6). */
export interface NeedsCrewProject {
  project_id: string;
  project_title: string;
  client_name: string;
  project_status: string;
}

/** A live pickup with no crew (no pickup block) — workstream C. */
export interface NeedsCrewPickup {
  pickup_id: string;
  display_title: string;
  status: string;
  scheduled_start: string | null;
  project_count: number;
}

/**
 * Committed-but-unscheduled tasks across all projects — surfaced on the board so
 * a scheduled project with an unplaced task can't be silently missed.
 */
/**
 * Reassign a PROJECT or PICKUP block between crew (dispatch-board drag, A6-2).
 * Takes the id in the mutate payload (unlike useReassignPickup(id)) so a single
 * onDragEnd can move any bar. Invalidate-only (the block model is reconciled
 * server-side; tasks keep their own optimistic hook).
 */
export function useReassignBlock() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { kind: 'project' | 'pickup'; id: string; from: string | null; to: string | null }
  >({
    mutationFn: ({ kind, id, from, to }) =>
      apiCall(`/${kind === 'project' ? 'projects' : 'pickups'}/${id}/reassign`, {
        method: 'POST',
        body: { from_crew_member_id: from, to_crew_member_id: to },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
    },
  });
}

export function useNeedsScheduling(enabled = true) {
  return useQuery({
    queryKey: ['dispatch', 'needs-scheduling'],
    queryFn: () =>
      apiCall<{ tasks: NeedsSchedulingTask[]; projects: NeedsCrewProject[]; pickups: NeedsCrewPickup[] }>(
        '/dispatch/needs-scheduling',
      ),
    staleTime: 10_000,
    refetchInterval: 60_000,
    enabled,
  });
}
